import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import logoAlbaker from "@/assets/logo-albaker.png";
import {
  Home, Users, FileText, MessageSquare, Settings as SettingsIcon, Share2, BarChart3,
  LogOut, Globe, Film, Hash, ListOrdered, Building2,
} from "lucide-react";

const links = [
  { to: "/admin", label: "نظرة عامة", icon: BarChart3, end: true },
  { to: "/admin/members", label: "الأعضاء", icon: Users },
  { to: "/admin/articles", label: "المقالات", icon: FileText },
  { to: "/admin/comments", label: "التعليقات", icon: MessageSquare },
];

const contentLinks = [
  { to: "/admin/content/about", label: "رحلتي في الإعلام", icon: Film },
  { to: "/admin/content/stats", label: "الأرقام", icon: Hash },
  { to: "/admin/content/timeline", label: "المشاريع", icon: ListOrdered },
  { to: "/admin/content/outlets", label: "الجهات الإعلامية", icon: Building2 },
];

const utilityLinks = [
  { to: "/admin/social", label: "التواصل الاجتماعي", icon: Share2 },
  { to: "/admin/settings", label: "إعدادات الموقع", icon: SettingsIcon },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div dir="rtl" className="min-h-screen bg-ink text-bone flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 border-l border-white/10 bg-ink-soft min-h-screen sticky top-0">
        <div className="p-8 border-b border-white/10">
          <Link to="/" className="inline-flex items-center gap-3 group" data-testid="admin-back-home">
            <img src={logoAlbaker} alt="" className="h-10 w-auto" />
            <span className="overline">albaker · admin</span>
          </Link>
          <div className="mt-6 text-sm font-arabic text-white/70 truncate">{user?.name}</div>
          <div className="text-xs text-white/40 truncate" dir="ltr">{user?.email}</div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-white/60 hover:text-gold hover:bg-white/5 transition-colors text-sm"
            data-testid="admin-nav-home"
          >
            <Globe size={16} strokeWidth={1.5} />
            <span className="font-arabic">العودة للموقع</span>
          </Link>

          {links.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border-r-2 border-gold" : "text-white/65 hover:text-gold hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-arabic">{label}</span>
            </NavLink>
          ))}

          <div className="pt-6 pb-2 px-4 text-[10px] tracking-[0.25em] uppercase text-white/35 font-arabic">محتوى الصفحة الرئيسية</div>
          {contentLinks.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`admin-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border-r-2 border-gold" : "text-white/65 hover:text-gold hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-arabic">{label}</span>
            </NavLink>
          ))}

          <div className="pt-6 pb-2 px-4 text-[10px] tracking-[0.25em] uppercase text-white/35 font-arabic">الإعدادات</div>
          {utilityLinks.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`admin-nav-${to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive ? "bg-gold/10 text-gold border-r-2 border-gold" : "text-white/65 hover:text-gold hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-arabic">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/65 hover:text-red-400 hover:bg-white/5 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span className="font-arabic">تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 glass-nav border-b border-white/10">
        <div className="container-x h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-3"><img src={logoAlbaker} alt="" className="h-8 w-auto" /><span className="overline">admin</span></Link>
          <button onClick={onLogout} className="text-white/60 hover:text-red-400" aria-label="logout"><LogOut size={18} /></button>
        </div>
        <nav className="flex overflow-x-auto px-4 pb-3 gap-2">
          <Link to="/" className="overline whitespace-nowrap px-3 py-1.5 border border-white/10 hover:border-gold hover:text-gold"><Home size={12} className="inline -mt-0.5 me-1" />الموقع</Link>
          {[...links, ...contentLinks, ...utilityLinks].map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end || to === "/admin"} className={({ isActive }) =>
              `overline whitespace-nowrap px-3 py-1.5 border ${isActive ? "border-gold text-gold" : "border-white/10 text-white/60 hover:border-gold hover:text-gold"}`
            }>
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 min-w-0 lg:pt-0 pt-32">
        <div className="container-x py-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
